import { useCallback, useEffect, useState } from 'react';
import { Shield, Loader2, Mail, KeyRound, UserPlus, LogOut, Check, X } from 'lucide-react';

/*
|------------------------------------------------------------------------------
| Internal-only access gate
|------------------------------------------------------------------------------
| Wraps the entire SPA. When VITE_INTERNAL_ONLY === '1' a visitor must prove
| they are on the internal allow-list before any of the product renders.
|
| This is a PERIMETER, not the product's auth. It answers "may this browser
| see the door", while NACIS roles answer "what may you do once inside". The
| two are deliberately separate: it stays correct even if app auth changes,
| and it makes no API calls into the product.
|
| OTP-only, no password — a shared environment password ends up in a chat
| thread; a code emailed to an allow-listed address does not. A browser that
| verifies once is remembered, so the code is a first-visit cost only.
|
| Not on the list → a request form that notifies the internal admin, who
| approves or rejects from ?internal=requests.
*/

// The gate lives at /api/internal-access, OUTSIDE the versioned product API,
// so derive the origin from VITE_API_URL (…/api/v1) and rebuild the path.
// Falls back to relative URLs in local dev, where Vite proxies /api.
let API_ORIGIN = '';
try {
  if (import.meta.env.PROD && import.meta.env.VITE_API_URL) {
    API_ORIGIN = new URL(import.meta.env.VITE_API_URL).origin;
  }
} catch {
  API_ORIGIN = '';
}
const API_BASE = `${API_ORIGIN}/api/internal-access`;

const TOKEN_KEY     = 'nacis.internal_token';
const DEVICE_ID_KEY = 'nacis.internal_device_id';

// Read once at module scope: Vite inlines it at build time, so it is constant
// for a given bundle. Keeping it out of the component means the passthrough
// below contains no hooks at all, and cannot produce a hook-order mismatch.
const INTERNAL_ONLY = import.meta.env.VITE_INTERNAL_ONLY === '1';

const REVIEW_MODE = typeof window !== 'undefined'
  && new URLSearchParams(window.location.search).get('internal') === 'requests';

function uuid() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return 'xxxxxxxxxxxx4xxxyxxxxxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function deviceId() {
  let id = localStorage.getItem(DEVICE_ID_KEY);
  if (!id) { id = uuid(); localStorage.setItem(DEVICE_ID_KEY, id); }
  return id;
}

async function sha256Hex(str) {
  const buf = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(str));
  return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join('');
}

/* Stable-ish browser signal, combined with the local id. Not a security
   boundary — it only decides whether this browser may skip the OTP. */
function fingerprintSource() {
  const n = window.navigator;
  return [
    n.userAgent, n.language, n.platform,
    `${window.screen?.width}x${window.screen?.height}x${window.screen?.colorDepth}`,
    Intl.DateTimeFormat().resolvedOptions().timeZone,
  ].join('|');
}

function deviceLabel() {
  const ua = window.navigator.userAgent;
  const browser = /Edg/.test(ua) ? 'Edge' : /Chrome/.test(ua) ? 'Chrome'
    : /Firefox/.test(ua) ? 'Firefox' : /Safari/.test(ua) ? 'Safari' : 'Browser';
  const os = /Windows/.test(ua) ? 'Windows' : /Mac OS/.test(ua) ? 'macOS'
    : /Android/.test(ua) ? 'Android' : /iPhone|iPad/.test(ua) ? 'iOS'
    : /Linux/.test(ua) ? 'Linux' : 'Unknown OS';
  return `${browser} on ${os}`;
}

async function call(path, { method = 'POST', body, token } = {}) {
  const res = await fetch(`${API_BASE}/${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  let data = {};
  try { data = await res.json(); } catch { /* non-JSON error page */ }
  return { ok: res.ok, status: res.status, data };
}

const post = (path, body, token) => call(path, { method: 'POST', body, token });
const get  = (path, token)       => call(path, { method: 'GET',  token });

/**
 * Passthrough wrapper. Contains no hooks, so builds with the gate disabled
 * mount the app directly with no behavioural difference at all.
 */
export default function InternalGate({ children }) {
  if (!INTERNAL_ONLY) return children;
  return <Gate>{children}</Gate>;
}

function Gate({ children }) {
  const [phase,      setPhase]      = useState('checking'); // checking|email|otp|request|requested|authed
  const [deviceHash, setDeviceHash] = useState(null);
  const [email,      setEmail]      = useState('');
  const [code,       setCode]       = useState('');
  const [form,       setForm]       = useState({ first_name: '', last_name: '', phone: '' });
  const [isAdmin,    setIsAdmin]    = useState(false);
  const [busy,       setBusy]       = useState(false);
  const [error,      setError]      = useState('');
  const [notice,     setNotice]     = useState('');

  // Resolve this browser's identity, then try to resume an existing session.
  useEffect(() => {
    let alive = true;
    (async () => {
      const dh = await sha256Hex(`${deviceId()}|${fingerprintSource()}`);
      if (!alive) return;
      setDeviceHash(dh);

      const token = localStorage.getItem(TOKEN_KEY);
      if (!token) { setPhase('email'); return; }

      const { ok, data } = await post('session', { device_hash: dh }, token);
      if (!alive) return;

      if (ok && data.valid) {
        setIsAdmin(Boolean(data.user?.is_admin));
        setPhase('authed');
      } else {
        localStorage.removeItem(TOKEN_KEY);
        setPhase('email');
      }
    })();
    return () => { alive = false; };
  }, []);

  const submitEmail = useCallback(async e => {
    e.preventDefault();
    setBusy(true); setError(''); setNotice('');

    const { ok, data } = await post('request-otp', {
      email: email.trim(), device_hash: deviceHash, device_label: deviceLabel(),
    });
    setBusy(false);

    if (!ok) {
      if (data.can_request) { setPhase('request'); return; }
      setError(data.message || 'Could not sign in.');
      return;
    }
    if (data.status === 'authenticated') {
      localStorage.setItem(TOKEN_KEY, data.token);
      setIsAdmin(Boolean(data.user?.is_admin));
      setPhase('authed');
      return;
    }
    setNotice(`We emailed a 6-digit code to ${data.email || email}.`);
    setPhase('otp');
  }, [email, deviceHash]);

  const submitCode = useCallback(async e => {
    e.preventDefault();
    setBusy(true); setError('');

    const { ok, data } = await post('verify-otp', {
      email: email.trim(), code: code.trim(), device_hash: deviceHash, device_label: deviceLabel(),
    });
    setBusy(false);

    if (!ok) { setError(data.message || 'Could not verify that code.'); return; }

    localStorage.setItem(TOKEN_KEY, data.token);
    setIsAdmin(Boolean(data.user?.is_admin));
    setPhase('authed');
  }, [email, code, deviceHash]);

  const submitRequest = useCallback(async e => {
    e.preventDefault();
    setBusy(true); setError('');

    const { ok, data } = await post('request-access', { email: email.trim(), ...form });
    setBusy(false);

    if (!ok) { setError(data.message || 'Could not send that request.'); return; }
    setPhase('requested');
  }, [email, form]);

  const signOut = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    localStorage.removeItem(TOKEN_KEY);
    setPhase('email'); setIsAdmin(false); setCode(''); setNotice('');
    try { await post('logout', undefined, token); } catch { /* local state is what matters */ }
  }, []);

  // ── Through the gate ────────────────────────────────────────────────────
  if (phase === 'authed') {
    if (REVIEW_MODE && isAdmin) return <ReviewQueue onExit={signOut} />;
    return (
      <>
        {children}
        <GateBadge onSignOut={signOut} />
      </>
    );
  }

  if (phase === 'checking') {
    return (
      <Shell>
        <div className="flex items-center justify-center gap-3 py-6 text-sm text-slate-300">
          <Loader2 className="h-4 w-4 animate-spin" /> Checking access…
        </div>
      </Shell>
    );
  }

  if (phase === 'requested') {
    return (
      <Shell title="Request sent" subtitle="Someone will review it shortly.">
        <p className="text-sm leading-relaxed text-slate-300">
          We have passed your details to the NACIS team. You will get an email if
          your access is approved.
        </p>
        <button type="button" className="gate-btn-ghost mt-6" onClick={() => { setPhase('email'); setError(''); }}>
          Back
        </button>
      </Shell>
    );
  }

  if (phase === 'request') {
    return (
      <Shell
        title="Request access"
        subtitle={`${email} is not on the internal list.`}
        icon={UserPlus}
      >
        <form onSubmit={submitRequest} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <input
              className="gate-input" placeholder="First name" required
              value={form.first_name}
              onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))}
            />
            <input
              className="gate-input" placeholder="Last name" required
              value={form.last_name}
              onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))}
            />
          </div>
          <input
            className="gate-input" placeholder="Phone (optional)"
            value={form.phone}
            onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
          />
          {error && <GateError>{error}</GateError>}
          <button type="submit" className="gate-btn w-full" disabled={busy}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
            Send request
          </button>
          <button type="button" className="gate-btn-ghost w-full" onClick={() => { setPhase('email'); setError(''); }}>
            Back
          </button>
        </form>
      </Shell>
    );
  }

  if (phase === 'otp') {
    return (
      <Shell title="Enter your code" subtitle={notice} icon={KeyRound}>
        <form onSubmit={submitCode} className="space-y-3">
          <input
            className="gate-input text-center font-mono text-2xl tracking-[0.5em]"
            inputMode="numeric" autoComplete="one-time-code" maxLength={6}
            placeholder="000000" required autoFocus
            value={code}
            onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          />
          {error && <GateError>{error}</GateError>}
          <button type="submit" className="gate-btn w-full" disabled={busy || code.length !== 6}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            Verify
          </button>
          <button
            type="button" className="gate-btn-ghost w-full"
            onClick={() => { setPhase('email'); setCode(''); setError(''); setNotice(''); }}
          >
            Use a different email
          </button>
        </form>
        <p className="mt-5 text-xs leading-relaxed text-slate-500">
          Once verified, this browser is remembered — you will not be asked again.
        </p>
      </Shell>
    );
  }

  // phase === 'email'
  return (
    <Shell title="Internal access" subtitle="This environment is not public." icon={Mail}>
      <form onSubmit={submitEmail} className="space-y-3">
        <input
          type="email" className="gate-input" placeholder="you@example.com"
          required autoFocus autoComplete="email"
          value={email} onChange={e => setEmail(e.target.value)}
        />
        {error && <GateError>{error}</GateError>}
        <button type="submit" className="gate-btn w-full" disabled={busy || !deviceHash}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
          Continue
        </button>
      </form>
      <p className="mt-5 text-xs leading-relaxed text-slate-500">
        Enter the email you were given access with. If you do not have access yet,
        you can request it on the next step.
      </p>
    </Shell>
  );
}

// ── Admin review queue (?internal=requests) ──────────────────────────────────

function ReviewQueue({ onExit }) {
  const [rows,  setRows]  = useState(null);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    const { ok, data } = await get('requests', token);
    if (!ok) { setError(data.message || 'Could not load requests.'); return; }
    setRows(data.data || []);
  }, []);

  useEffect(() => { load(); }, [load]);

  const decide = useCallback(async (id, action) => {
    setBusyId(id);
    const token = localStorage.getItem(TOKEN_KEY);
    await post(`requests/${id}/${action}`, undefined, token);
    setBusyId(null);
    load();
  }, [load]);

  return (
    <Shell title="Access requests" subtitle="Approve to add someone to the internal list." wide>
      {error && <GateError>{error}</GateError>}

      {rows === null ? (
        <div className="flex items-center gap-2 py-6 text-sm text-slate-400">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : rows.length === 0 ? (
        <p className="py-6 text-sm text-slate-400">No requests yet.</p>
      ) : (
        <ul className="divide-y divide-white/10">
          {rows.map(r => (
            <li key={r.id} className="flex flex-wrap items-center justify-between gap-3 py-3">
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-100">
                  {r.first_name} {r.last_name}
                </div>
                <div className="truncate text-xs text-slate-400">
                  {r.email}{r.phone ? ` · ${r.phone}` : ''}
                </div>
              </div>
              {r.status === 'pending' ? (
                <div className="flex flex-shrink-0 items-center gap-2">
                  <button
                    type="button" className="gate-btn px-3 py-1.5 text-xs"
                    disabled={busyId === r.id} onClick={() => decide(r.id, 'approve')}
                  >
                    <Check className="h-3.5 w-3.5" /> Approve
                  </button>
                  <button
                    type="button" className="gate-btn-ghost px-3 py-1.5 text-xs"
                    disabled={busyId === r.id} onClick={() => decide(r.id, 'reject')}
                  >
                    <X className="h-3.5 w-3.5" /> Reject
                  </button>
                </div>
              ) : (
                <span className="flex-shrink-0 text-xs capitalize text-slate-400">
                  {r.status}{r.reviewed_by ? ` · ${r.reviewed_by}` : ''}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}

      <div className="mt-6 flex items-center gap-2">
        <a href="/" className="gate-btn-ghost">Back to app</a>
        <button type="button" className="gate-btn-ghost" onClick={onExit}>Sign out</button>
      </div>
    </Shell>
  );
}

// ── Chrome ───────────────────────────────────────────────────────────────────

/** Small persistent marker so nobody forgets they are inside a gated build. */
function GateBadge({ onSignOut }) {
  return (
    <div className="fixed bottom-3 left-3 z-[60] flex items-center gap-2 rounded-full bg-slate-900/90 px-3 py-1.5 text-[11px] font-medium text-slate-300 shadow-lg ring-1 ring-white/10 backdrop-blur">
      <Shield className="h-3 w-3 text-sky-400" />
      Internal access
      <button
        type="button" onClick={onSignOut}
        className="ml-1 inline-flex items-center gap-1 text-slate-400 transition hover:text-white"
        aria-label="Leave internal access"
      >
        <LogOut className="h-3 w-3" />
      </button>
    </div>
  );
}

function Shell({ title, subtitle, icon: Icon, children, wide }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-950 px-4 py-10">
      <div className={wide ? 'w-full max-w-2xl' : 'w-full max-w-sm'}>
        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-sky-600/15 ring-1 ring-sky-500/30">
            <Shield className="h-5 w-5 text-sky-400" strokeWidth={2.4} />
          </div>
          <div className="leading-tight">
            <div className="text-sm font-bold tracking-wide text-white">NACIS</div>
            <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">
              National AI Communications-Intelligence System
            </div>
          </div>
        </div>

        <div className="rounded-2xl bg-slate-900/70 p-6 shadow-2xl ring-1 ring-white/10">
          {title && (
            <div className="mb-5">
              <div className="flex items-center gap-2">
                {Icon && <Icon className="h-4 w-4 text-sky-400" />}
                <h1 className="text-lg font-semibold text-white">{title}</h1>
              </div>
              {subtitle && <p className="mt-1 text-sm text-slate-400">{subtitle}</p>}
            </div>
          )}
          {children}
        </div>

        <p className="mt-6 text-center text-[11px] text-slate-600">
          Access is limited to authorised NACIS personnel.
        </p>
      </div>
    </div>
  );
}

function GateError({ children }) {
  return (
    <div className="rounded-md bg-rose-500/10 px-3 py-2 text-sm text-rose-300 ring-1 ring-rose-500/30">
      {children}
    </div>
  );
}
